import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const partners = [
  {
    icon: "📣",
    title: "Advertisers & Brands",
    zh: "广告主与品牌方",
    desc: "Embed your brand naturally into our high-engagement short drama narratives. Reach 500K+ loyal SEA viewers with authentic in-content integration.",
    cta: "Explore Ad Solutions",
    points: ["In-drama brand integration", "Targeted audience segments", "Multi-language reach", "Performance analytics"],
  },
  {
    icon: "🌐",
    title: "Distribution Partners",
    zh: "内容发行合作商",
    desc: "License and distribute MIRE's proven content library across platforms, regions, and languages. Access a growing catalog of SEA market hits.",
    cta: "Distribution Inquiry",
    points: ["Exclusive regional rights", "Multi-platform licensing", "Revenue sharing model", "Ongoing content supply"],
  },
  {
    icon: "💡",
    title: "IP Licensing",
    zh: "IP授权合作方",
    desc: "Partner with MIRE to develop, adapt, or co-create IP from our successful title library. Long-term value from proven audience-tested stories.",
    cta: "IP Partnership",
    points: ["Adapt proven SEA stories", "Cross-market co-production", "Merchandise & derivatives", "Global IP development"],
  },
];

const Partnership = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="partnership" ref={ref} style={{
      padding: "100px 40px",
      background: "linear-gradient(180deg, #080808 0%, rgba(255,28,53,0.03) 50%, #080808 100%)",
      borderTop: "1px solid rgba(255,255,255,0.05)",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>B2B PARTNERSHIP · 商务合作</span>
          </div>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 16px" }}>
              Partner with<br /><span style={{ color: "#FF1C35" }}>Southeast Asia's</span> Leading<br />Entertainment Platform
            </h2>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 15, maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
              Join MIRE's ecosystem as an advertiser, distributor, or IP partner — and tap into the most engaged SEA digital entertainment audience.
            </p>
          </div>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 64 }} className="partner-grid">
          {partners.map((p, i) => (
            <motion.div key={p.title}
              initial={{ opacity: 0, y: 28 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.15, duration: 0.7 }}
              style={{
                padding: "32px 24px", borderRadius: 18,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
                display: "flex", flexDirection: "column",
                transition: "border-color 0.3s",
              }}
              whileHover={{ borderColor: "rgba(255,28,53,0.3)" } as never}
            >
              <div style={{ fontSize: 36, marginBottom: 18 }}>{p.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{p.title}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 14 }}>{p.zh}</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: 20, flex: 1 }}>{p.desc}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px" }}>
                {p.points.map(pt => (
                  <li key={pt} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
                    <span style={{ color: "#FF1C35", fontWeight: 700 }}>→</span> {pt}
                  </li>
                ))}
              </ul>
              <button onClick={scrollToContact}
                style={{
                  padding: "11px 20px", borderRadius: 8,
                  background: "rgba(255,28,53,0.1)", color: "#FF1C35",
                  border: "1px solid rgba(255,28,53,0.3)", fontSize: 13,
                  fontWeight: 700, cursor: "pointer", width: "100%",
                  transition: "background 0.2s",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,28,53,0.2)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,28,53,0.1)")}
              >
                {p.cta} →
              </button>
            </motion.div>
          ))}
        </div>

        {/* Lions Club badge */}
        <motion.div initial={{ opacity: 0 }} animate={visible ? { opacity: 1 } : {}} transition={{ delay: 0.6 }}
          style={{
            padding: "28px 36px", borderRadius: 16,
            background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)",
            display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap",
          }}>
          <div style={{ fontSize: 48 }}>🦁</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 4 }}>Lions Club International Partnership</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>
              One of the world's largest international organizations — 200+ countries. MIRE leverages global business & government networks to connect content with international commercial opportunities. Offices in London, Singapore & New York.
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {["🇬🇧 London", "🇸🇬 Singapore", "🇺🇸 New York"].map(c => (
              <span key={c} style={{ padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{c}</span>
            ))}
          </div>
        </motion.div>
      </div>
      <style>{`
        @media (max-width: 900px) { .partner-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
};

export default Partnership;
